/**
 * Dates that came from the API, rendered as the days they actually are.
 *
 * Every test date we display is a Postgres `DATE` column (`recent_test_date`,
 * `latest_test_date`, `test_date` — see database/init/02-schema.sql), which
 * serializes as "2024-03-12": a calendar day, with no time and no zone.
 *
 * `new Date("2024-03-12")` does not treat that as a calendar day. The
 * ECMAScript spec says a date-only form is UTC, so it becomes midnight UTC —
 * which is the evening of March 11th in every US timezone. The app was
 * reporting the wrong day for water tests to its entire user base, one day
 * early, everywhere a date appeared.
 *
 * Reading the parts and building a local date pins it to local midnight, which
 * is what a bare calendar date means to the person reading it. Full timestamps
 * are left alone: those are real instants and should be shown in local time.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const DEFAULT_OPTIONS = { year: "numeric", month: "short", day: "numeric" };

/**
 * Parse an API date value into a Date, without the UTC shift for date-only
 * strings.
 * @returns {Date|null} null for anything unparseable, so callers can choose
 *   their own fallback rather than rendering "Invalid Date".
 */
export const parseApiDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const match = DATE_ONLY.exec(String(value).trim());
  const parsed = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Human-readable date for display. Defaults to "Mar 12, 2024".
 * @returns {string|null} null when there is nothing to show.
 */
export const formatApiDate = (value, options = DEFAULT_OPTIONS) => {
  const parsed = parseApiDate(value);
  return parsed ? parsed.toLocaleDateString("en-US", options) : null;
};

/** The calendar year of an API date, or null. */
export const apiDateYear = (value) => parseApiDate(value)?.getFullYear() ?? null;
